import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Music2 } from 'lucide-react';
import { routes } from '@/lib/routes';
import { AppShell } from '@/components/layout/AppShell';
import { ChatWindow } from '@/tools/inner-team/components/chat/ChatWindow';
import { ChatInput } from '@/tools/inner-team/components/chat/ChatInput';
import { CharacterPortrait } from '@/tools/inner-team/components/team/CharacterPortrait';
import { useEffect } from 'react';
import { useChat } from '@/hooks/useChat';
import { useChatStore } from '@/store/useChatStore';
import { useTeamStore } from '@/store/useTeamStore';
import { LoginPromptDialog } from '@/components/auth/LoginPromptDialog';
import { VoucherPromptDialog } from '@/components/auth/VoucherPromptDialog';
import type { ChatMessage } from '@/types';

export function MemberConsultChat() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const sessions = useChatStore((s) => s.sessions);
  const setActiveSession = useChatStore((s) => s.setActiveSession);
  const members = useTeamStore((s) => s.members);

  const session = sessions.find((s) => s.id === id);
  const member = session ? members.find((m) => m.id === session.memberIds[0]) : undefined;

  useEffect(() => {
    if (id) setActiveSession(id);
  }, [id, setActiveSession]);

  const { messages, isStreaming, sendMessage, loginRequired, dismissLoginPrompt, voucherRequired, dismissVoucherPrompt } = useChat(id ?? '', 'member-consult', member ? { memberId: member.id } : undefined);

  if (!session || !member) {
    navigate(routes.innerTeam.teamDashboard(), { replace: true });
    return null;
  }

  const greeting: ChatMessage = {
    id: '__greeting__',
    role: 'assistant',
    content: `Hallo! Ich bin ${member.name} — ${member.role}. Was beschäftigt dich gerade?`,
    timestamp: new Date().toISOString(),
  };

  return (
    <AppShell>
      <div className="flex items-center gap-3 px-4 h-14 shrink-0 border-b border-border bg-background/80 backdrop-blur-sm">
        <button
          onClick={() => navigate(routes.innerTeam.memberDetail(member.id))}
          className="w-8 h-8 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          aria-label="Zurück"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="rounded-full ring-2 ring-background shrink-0">
          <CharacterPortrait
            emoji={member.avatarEmoji}
            color={member.avatarColor}
            avatarImageUrl={member.avatarImageUrl}
            size="xs"
          />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold truncate">{member.name}</p>
          <p className="text-xs text-muted-foreground truncate">{member.role}</p>
        </div>
      </div>

      {member.spotifyTrackId && (
        <div className="px-4 pt-3 pb-2 shrink-0 bg-background">
          <div className="max-w-lg mx-auto space-y-1.5">
            <p className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
              <Music2 size={12} /> {member.name}'s Song
            </p>
            <iframe
              src={`https://open.spotify.com/embed/track/${member.spotifyTrackId}?utm_source=innerteam`}
              width="100%"
              height="80"
              frameBorder="0"
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
              className="rounded-lg block"
              title={`Musik von ${member.name}`}
            />
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col overflow-hidden">
        <ChatWindow messages={messages} isStreaming={isStreaming} greeting={greeting} />
        <ChatInput onSend={sendMessage} disabled={isStreaming} />
      </div>
      <LoginPromptDialog open={loginRequired} onOpenChange={(open) => { if (!open) dismissLoginPrompt(); }} />
      <VoucherPromptDialog open={voucherRequired} onOpenChange={(open) => { if (!open) dismissVoucherPrompt(); }} />
    </AppShell>
  );
}
